import { clearImpersonation } from '../utils/impersonation';
import { writable, derived } from 'svelte/store';

export interface CompanyInfo {
  company_id: number;
  company_name: string;
  company_type: string;
}

export interface PropertyInfo {
  property_id: number;
  property_name: string | null;
  role: string;
}

export interface UserProfile {
  impersonation?: { session_id: string; actor_id: number; target_id: number; expires_at: string };
  user_id: number;
  user_type: 'system_admin' | 'customer' | 'property_manager' | 'tenant' | 'manufacturer' | 'observer' | 'installer';
  name: string;
  email: string | null;
  company?: CompanyInfo;
  customer_accounts?: {account_id:number;name:string;role:'primary_representative'|'administrator'|'property_manager'|'member';access_scope:'selected_properties'|'all_properties'}[];
  properties: PropertyInfo[];
  preferences: Record<string, any>;
}

export const user = writable<UserProfile | null>(null);

// Convenience derived stores
export const userType = derived(user, ($user) => $user?.user_type ?? null);
export const isAdmin = derived(user, ($user) =>
  $user?.user_type === 'system_admin'
);
export const showConsumption = derived(user, ($user) =>
  $user?.user_type === 'customer' || $user?.user_type === 'property_manager' || $user?.user_type === 'tenant'
);

/** Fetch the user profile from the API and populate the store. Returns true if authenticated. */
export async function fetchUserProfile(): Promise<boolean> {
  try {
    const res = await fetch('/api/me');
    if (res.ok) {
      const profile: UserProfile = await res.json();
      user.set(profile);
      return true;
    }
    user.set(null);
    return false;
  } catch {
    user.set(null);
    return false;
  }
}

/** Clear the user store (on logout). */
export function clearUser() {
  try {
    localStorage.removeItem('auth_hint');
  } catch {}
  user.set(null);
}

/** Complete logout flow: backend call + store clear. */
export async function logout(): Promise<void> {
  try {
    await fetch('/api/logout', { method: 'POST' });
  } catch (e) {
    console.error('Logout request failed:', e);
  } finally {
    clearImpersonation();
    clearUser();
  }
}
